import wasSentWithStaleAccessToken from '@client-shared/utils/was-sent-with-stale-access-token'
import wasSentWithStaleRefreshToken from '@client-shared/utils/was-sent-with-stale-refresh-token'

import sendRaygun from '@/utils/send-raygun.js'

// routes that render a 403 inline instead of being redirected to the projects
// list — pages with their own forbidden-state UI.
//
// The two mailer/invite entries are NOT redundant with the skipForbiddenRedirect
// flag below: that flag only covers the read-only preview request, while these
// cover the real SEND/INVITE request. Without them a 403 from POST /mail or
// POST /participants unmounts the page before ItemMailer's SEND_ERROR /
// new.vue's SUBMIT_ERROR can render, and the mailer's beforeRouteEnter then runs
// mailer/FULL_RESET — the composed mail is gone and no error is ever shown.
const ROUTES_WITH_INLINE_403_HANDLING = [
  'projects-projectId-plans',
  'projects-projectId-plans-planId',
  'projects-projectId-documents',
  'projects-projectId-documents-documentId',
  'projects-projectId-tasks-taskId',
  'projects-projectId-photos',
  'projects-projectId-photos-photoId',
  'projects-projectId-mailer',
  'projects-projectId-participants-new',
  'download-downloadId',
]

export default ({ $axios, app }) => {
  $axios.interceptors.response.use(undefined, async error => {
    const router = app.config.globalProperties.$router
    const route = app.config.globalProperties.$route
    const $auth = app.config.globalProperties.$auth

    // Canceled requests reject like every other branch below. Resolving them with undefined used to
    // make a superseded request indistinguishable from a 200 with an empty body, which silently
    // broke every caller that told those apart (see ignore-canceled-request.js). Raygun stays quiet
    // either way: send-raygun.js drops cancels via isCancel, and raygun.js onBeforeSend drops
    // CanceledError payloads.
    if (error && $axios.isCancel(error) && !error.response?.status) {
      return Promise.reject(error)
    }

    if (error && !error.response) {
      await sendRaygun({
        error,
        tags: [ 'axios-no-error-response' ],
      })

      return Promise.reject(error)
    }

    if (error.response.status === 403 && error.response.data?.error === 'TWO_FACTOR_AUTH_REQUIRED') {
      return Promise.reject(error)
    }

    // a read-only email preview must never navigate the user away: the redirect
    // below would unmount the mailer/invite form mid-flight and silently destroy
    // the composed mail (selected items, recipients, body text) before the
    // component's own catch can render the error. The preview api helpers
    // declare this intent via the skipForbiddenRedirect axios config flag.
    if (error.response.status === 403 && error.config?.skipForbiddenRedirect) {
      return Promise.reject(error)
    }

    if (
      error.response.status === 403 &&
      !ROUTES_WITH_INLINE_403_HANDLING.includes(route.name)
    ) {
      await router.push({
        name: 'projects',
      })

      return Promise.reject(error)
    }

    if (error.response.status === 401 && $auth.loggedIn && route.name !== 'download-downloadId') {
      // Never log out over a 401 the app earned with a token it has already replaced -- see the
      // module for why. Confirming 2FA invalidates every older token server-side, so every
      // request still in flight at that moment comes back 401 and used to end the session that
      // had just enabled 2FA.
      // 🚨 Has to match ionsrc/plugins/axios-interceptors/error-handler.js
      //
      // A 401 can also arrive while such a rotation is still on the wire, before its new tokens are applied: decide
      // once they are (see pendingTokenRotation in the auth plugin; the rotation request itself must not wait for
      // itself).
      if (!error.config?.isTokenRotation) {
        await $auth.waitForSentTokenRotation()

        // The rotation may have ended the session meanwhile (its own 401 logged out): a second logout would
        // replace the return path the first one stored
        if (!$auth.loggedIn) {
          return Promise.reject(error)
        }
      }

      if (wasSentWithStaleAccessToken({
        requestHeaders: error.config?.headers,
        accessToken: $auth.accessToken,
      }) || wasSentWithStaleRefreshToken({
        requestConfig: error.config,
        refreshToken: $auth.refreshToken,
      })) {
        return Promise.reject(error)
      }

      await $auth.logout()

      return Promise.reject(error)
    }

    // expected outcomes of a mail preview, not faults: a stale client state
    // (TASK_HAS_NO_ASSIGNEE, PARTICIPANT_ALREADY_EXISTS), a payload the server
    // refuses, or the 20-request preview limiter, which the preview flows reach
    // just by clicking through the recipients. All of them are rendered inline
    // by the modal and logged server-side, so reporting them here only buries
    // the genuine failures under click noise. Placed after the 401 branch so an
    // expired session still logs the user out, and before the 5xx branch so
    // real server faults on a preview stay reported.
    if (error.config?.skipForbiddenRedirect && error.response.status < 500) {
      return Promise.reject(error)
    }

    // log 500er separately
    if (error.response.status === 500) {
      await sendRaygun({
        error,
        tags: [ 'axios-on-error-500' ],
      })

      return Promise.reject(error)
    }

    // a rate limiter refused the request: the budget that hit and how long it
    // blocks go along (server/README.md "Rate limiting").
    // 🚨 Has to match ionsrc/plugins/axios-interceptors/error-handler.js
    if (error.response.status === 429) {
      await sendRaygun({
        error,
        tags: [ 'axios-on-error-rate-limit-reached' ],
        customData: {
          retryAfter: error.response.headers?.['retry-after'],
          rateLimit: error.response.headers?.ratelimit,
        },
      })

      return Promise.reject(error)
    }

    // do not log 401 auth or 403 forbidden´
    if (error.response.status !== 401 && error.response.status !== 403) {
      await sendRaygun({
        error,
        tags: [ 'axios-on-error' ],
      })

      return Promise.reject(error)
    }

    return Promise.reject(error)
  })
}
